import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Phone, ArrowRight, CheckCircle2, TrendingUp, Briefcase, Wallet, ShieldCheck,
  LineChart, PiggyBank, Building2, Handshake, Users, Target, Gauge, BadgeDollarSign,
} from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/services/part-time-cfo")({
  head: () => ({
    meta: [
      ...routeMeta("/services/part-time-cfo"),
      { title: "Part-Time CFO Services | CCOB & Company" },
      { name: "description", content: "Professional CFO-level financial guidance at a fraction of the cost of a full-time hire — budgeting, cash flow, tax strategy, and long-term planning." },
      { property: "og:title", content: "Part-Time CFO Services — CCOB & Company PA" },
      { property: "og:description", content: "Strategic CFO expertise without the full-time price tag." },
    ],
    links: canonicalLinks("/services/part-time-cfo"),
  }),
  component: CFOPage,
});

const benefits = [
  { icon: Wallet, title: "Fraction of the Cost", desc: "Get senior CFO expertise without the salary, benefits, and overhead of a full-time hire." },
  { icon: Gauge, title: "Better Financial Visibility", desc: "Clear dashboards and reporting so you always know where your business stands." },
  { icon: Target, title: "Smarter Decisions", desc: "Data-driven guidance on pricing, hiring, capital expenses, and growth bets." },
  { icon: ShieldCheck, title: "Stronger Controls", desc: "Reduce risk with proper financial oversight, segregation of duties, and review cadence." },
];

const oversight = [
  { icon: PiggyBank, title: "Budgeting & Forecasting", desc: "Annual budgets, rolling forecasts, and variance analysis you can actually act on." },
  { icon: LineChart, title: "Profitability Analysis", desc: "Understand margin by product, service, customer, or location — and where to focus next." },
  { icon: Wallet, title: "Cash Flow Management", desc: "Weekly and monthly cash flow planning so you never get caught by a shortfall." },
  { icon: BadgeDollarSign, title: "Tax-Saving Strategies", desc: "Year-round coordination with our tax team to legally minimize your liability." },
  { icon: ShieldCheck, title: "Financial Controls & Oversight", desc: "Policies, approvals, and review processes that protect your business and team." },
  { icon: Users, title: "Accounting Staff Management", desc: "Mentor, manage, and elevate your in-house bookkeepers and accountants." },
];

const growth = [
  { icon: Building2, title: "Financing & Banking Relationships", desc: "Loan packages, lender introductions, and ongoing banker communication on your behalf." },
  { icon: TrendingUp, title: "Long-Term Business Planning", desc: "Multi-year strategic plans tied to clear financial milestones." },
  { icon: Handshake, title: "Mergers & Acquisitions Support", desc: "Buy-side and sell-side guidance, due diligence, and deal structuring." },
  { icon: Briefcase, title: "Board & Investor Reporting", desc: "Polished reporting packages for boards, partners, and investors." },
];

const whyNeedIt = [
  "You're growing fast and your books can't keep up",
  "You don't know your true profit by product or service",
  "Cash flow feels unpredictable month to month",
  "You need financing but lack the reporting to get it",
  "You're considering an acquisition or sale",
  "You can't justify a $200K+ full-time CFO yet",
];

function CFOPage() {
  const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };
  return (
    <>
      <PageHero
        eyebrow="Part-Time CFO"
        title="Part-Time CFO Services"
        subtitle="Senior-level financial leadership for growing businesses — without the full-time salary."
        image={img.heroAccounting}
      />

      {/* INTRO */}
      <section className="py-24">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Why Businesses Need It</div>
            <h2 className="font-display text-4xl md:text-5xl text-primary leading-[1.05]">
              CFO-level guidance, <span className="italic text-gold">at a fraction of the cost</span>.
            </h2>
            <div className="gold-line mt-5" />
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Most growing businesses reach a point where the bookkeeper isn't enough — but a full-time CFO at $200K+ per year doesn't make sense yet. Our Part-Time CFO service bridges that gap, giving you experienced financial leadership exactly when and how you need it.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              You get a strategic partner focused on visibility, profitability, and long-term growth — not just compliance.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={`tel:${site.phoneRaw}`} className="btn-primary"><Phone className="h-4 w-4" /> Call Us <ArrowRight className="h-4 w-4" /></a>
              <Link to="/contact" className="btn-outline">Schedule a Consultation</Link>
            </div>
          </motion.div>
          <motion.ul {...fadeUp} className="lg:col-span-5 bg-secondary/40 border border-border rounded-2xl p-7 space-y-3">
            <div className="text-gold uppercase tracking-[0.25em] text-[11px] mb-2">Signs You Need a Part-Time CFO</div>
            {whyNeedIt.map((it) => (
              <li key={it} className="flex items-start gap-3 text-sm text-foreground/85">
                <CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {it}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-secondary/40">
        <div className="container-x">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Benefits</div>
            <h2 className="font-display text-3xl md:text-4xl text-primary">What you get with a Part-Time CFO</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b) => (
              <motion.div key={b.title} {...fadeUp} className="bg-card border border-border rounded-2xl p-6 hover:border-gold transition-colors">
                <div className="h-11 w-11 rounded-full grid place-items-center bg-primary text-primary-foreground"><b.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                <div className="mt-4 font-display text-lg text-primary">{b.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINANCIAL OVERSIGHT & STRATEGY */}
      <section className="py-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">What's Included</div>
            <h2 className="font-display text-4xl text-primary">Financial Oversight & Strategy</h2>
            <div className="gold-line mt-5" />
            <p className="mt-5 text-muted-foreground">The day-to-day and month-to-month financial leadership that keeps your business healthy.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {oversight.map((it) => (
              <motion.div key={it.title} {...fadeUp} className="bg-card border border-border rounded-2xl p-7 hover:border-gold transition-colors">
                <div className="flex gap-4">
                  <div className="h-11 w-11 rounded-full grid place-items-center bg-primary text-primary-foreground shrink-0"><it.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="font-display text-xl text-primary">{it.title}</h3>
                    <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS GROWTH SUPPORT */}
      <section className="py-24 bg-secondary/40">
        <div className="container-x">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Looking Ahead</div>
            <h2 className="font-display text-4xl text-primary">Business Growth Support</h2>
            <div className="gold-line mt-5" />
            <p className="mt-5 text-muted-foreground">Strategic CFO involvement in the moments that shape your company's future.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {growth.map((it) => (
              <motion.div key={it.title} {...fadeUp} className="bg-card border border-border rounded-2xl p-7 hover:border-gold transition-colors">
                <div className="flex gap-4">
                  <div className="h-11 w-11 rounded-full grid place-items-center bg-primary text-primary-foreground shrink-0"><it.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="font-display text-xl text-primary">{it.title}</h3>
                    <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 pt-20">
        <div className="container-x">
          <div className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl">Ready for CFO-level clarity?</h2>
            <p className="mt-3 opacity-90 max-w-xl mx-auto">Let's talk about what financial leadership could look like inside your business — without the full-time commitment.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <a href={`tel:${site.phoneRaw}`} className="btn-gold"><Phone className="h-4 w-4" /> Call {site.phone}</a>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-primary-foreground/30 hover:border-gold hover:text-gold transition-colors">Schedule a Consultation <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
